import React, { useState, useEffect, useCallback } from 'react';
import { getFornecedores, deleteFornecedor, addFornecedor, consultarCNPJ } from '../services/api';
import FornecedorEditModal from '../components/FornecedorEditModal';
import LancamentosTable from '../components/LancamentosTable';
import { formatCpfCnpj, validateCpfCnpj } from '../utils/validationUtils';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    Snackbar,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Autocomplete, Tabs, Tab, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Fornecedor } from '../services/types';

const FornecedoresPage: React.FC = () => {
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [fornecedorToEdit, setFornecedorToEdit] = useState<Fornecedor | null>(null);
    const [tabValue, setTabValue] = useState(0);

    // Snackbar State
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setIsSnackbarOpen(true);
    };

    // Lancamentos Modal State
    const [isLancamentosModalOpen, setIsLancamentosModalOpen] = useState(false);
    const [selectedFornecedorLancamentos, setSelectedFornecedorLancamentos] = useState<Fornecedor | null>(null);

    // Add Modal State
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [newNome, setNewNome] = useState('');
    const [newCnpj, setNewCnpj] = useState('');
    const [newTelefone, setNewTelefone] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newEndereco, setNewEndereco] = useState('');
    const [newTipo, setNewTipo] = useState<'MATERIAL' | 'SERVICO' | 'AMBOS'>('SERVICO');

    const [cpfCnpjError, setCpfCnpjError] = useState('');
    const [isConsulting, setIsConsulting] = useState(false);

    const fetchFornecedores = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getFornecedores();
            setFornecedores(data || []);
        } catch (err) {
            console.error('Erro ao buscar fornecedores:', err);
            showSnackbar('Erro ao buscar dados dos fornecedores.', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFornecedores();
    }, [fetchFornecedores]);

    const handleDelete = async (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir este fornecedor?')) {
            try {
                await deleteFornecedor(id);
                showSnackbar('Fornecedor excluído com sucesso.', 'success');
                fetchFornecedores();
            } catch (error) {
                console.error(error);
                showSnackbar('Erro ao excluir fornecedor.', 'error');
            }
        }
    };

    const handleEditClick = (fornecedor: Fornecedor) => {
        setFornecedorToEdit(fornecedor);
        setEditModalOpen(true);
    };

    const handleViewLancamentosClick = (fornecedor: Fornecedor) => {
        setSelectedFornecedorLancamentos(fornecedor);
        setIsLancamentosModalOpen(true);
    };

    const handleAddClick = () => {
        setNewNome('');
        setNewCnpj('');
        setNewTelefone('');
        setNewEmail('');
        setNewEndereco('');
        setNewTipo('SERVICO');
        setCpfCnpjError('');
        setAddModalOpen(true);
    };

    const handleCnpjCpfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        const formattedValue = formatCpfCnpj(rawValue);
        setNewCnpj(formattedValue);

        if (formattedValue.length > 0) {
            // Verifica duplicidade local
            const isDuplicate = fornecedores.find(f => f.cnpj_cpf === formattedValue);
            if (isDuplicate) {
                setCpfCnpjError(`Já cadastrado: ${isDuplicate.nome_razao_social}`);
                return;
            }

            const isValid = validateCpfCnpj(formattedValue);
            if (!isValid) {
                setCpfCnpjError('CPF ou CNPJ inválido');
            } else {
                setCpfCnpjError('');
                const cleanValue = formattedValue.replace(/\D/g, '');
                if (cleanValue.length === 14) {
                    setIsConsulting(true);
                    try {
                        const data = await consultarCNPJ(cleanValue);
                        if (data.razao_social) setNewNome(data.razao_social);
                        
                        let enderecoStr = '';
                        if (data.logradouro) enderecoStr += data.logradouro;
                        if (data.numero) enderecoStr += `, ${data.numero}`;
                        if (data.bairro) enderecoStr += ` - ${data.bairro}`;
                        if (data.municipio && data.uf) enderecoStr += ` - ${data.municipio}/${data.uf}`;
                        if (enderecoStr) setNewEndereco(enderecoStr);

                        if (data.ddd_telefone_1) setNewTelefone(data.ddd_telefone_1);

                    } catch (error) {
                        console.error('Falha ao consultar CNPJ', error);
                        // Não mostramos snackbar aqui para não incomodar se a API falhar silenciosamente
                    } finally {
                        setIsConsulting(false);
                    }
                }
            }
        } else {
            setCpfCnpjError('');
        }
    };

    const handleSaveNewFornecedor = async () => {
        if (!newNome.trim() || !newCnpj.trim()) {
            showSnackbar('Nome e CNPJ/CPF são obrigatórios.', 'warning');
            return;
        }

        if (newCnpj && !validateCpfCnpj(newCnpj)) {
            setCpfCnpjError('Corrija o CPF ou CNPJ antes de salvar.');
            return;
        }

        try {
            await addFornecedor({
                nome_razao_social: newNome,
                cnpj_cpf: newCnpj,
                telefone: newTelefone,
                email: newEmail,
                endereco: newEndereco,
                tipo: newTipo
            });
            setAddModalOpen(false);
            showSnackbar('Fornecedor adicionado com sucesso!', 'success');
            fetchFornecedores();
        } catch (error) {
            console.error('Erro ao adicionar fornecedor:', error);
            showSnackbar('Erro ao adicionar fornecedor. Verifique se o documento já existe no banco.', 'error');
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Fornecedores
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ borderRadius: 2 }}
                >
                    Novo Fornecedor
                </Button>
            </Box>

            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    centered
                >
                    <Tab label="Todos" />
                    <Tab label="Serviços" />
                    <Tab label="Materiais" />
                </Tabs>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                    <CircularProgress />
                </Box>
            ) : fornecedores.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Nenhum fornecedor encontrado.</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={handleAddClick}>
                        Adicionar o primeiro fornecedor
                    </Button>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={2}>
                    <Table sx={{ minWidth: 650 }} aria-label="tabela de fornecedores">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell><strong>Nome / Razão Social</strong></TableCell>
                                <TableCell><strong>Tipo</strong></TableCell>
                                <TableCell><strong>CNPJ / CPF</strong></TableCell>
                                <TableCell><strong>Telefone</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Endereço</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fornecedores
                                .filter(f => {
                                    if (tabValue === 0) return true;
                                    if (tabValue === 1) return f.tipo === 'SERVICO' || f.tipo === 'AMBOS' || !f.tipo; // Default to SERVICO if undefined
                                    if (tabValue === 2) return f.tipo === 'MATERIAL' || f.tipo === 'AMBOS';
                                    return true;
                                })
                                .map((fornecedor) => (
                                    <TableRow
                                        key={fornecedor.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'action.hover' } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {fornecedor.nome_razao_social}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={fornecedor.tipo || 'SERVICO'}
                                                size="small"
                                                color={fornecedor.tipo === 'MATERIAL' ? 'secondary' : fornecedor.tipo === 'AMBOS' ? 'info' : 'primary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{fornecedor.cnpj_cpf}</TableCell>
                                        <TableCell>{fornecedor.telefone || '-'}</TableCell>
                                        <TableCell>{fornecedor.email || '-'}</TableCell>
                                        <TableCell>{fornecedor.endereco || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <Tooltip title="Ver Lançamentos">
                                                    <IconButton size="small" color="primary" onClick={() => handleViewLancamentosClick(fornecedor)}>
                                                        <ListAltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Editar">
                                                    <IconButton size="small" onClick={() => handleEditClick(fornecedor)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(fornecedor.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add Modal */}
            <Dialog open={isAddModalOpen} onClose={() => setAddModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Adicionar Novo Fornecedor</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="CNPJ/CPF"
                            fullWidth
                            value={newCnpj}
                            onChange={handleCnpjCpfChange}
                            required
                            error={!!cpfCnpjError}
                            helperText={cpfCnpjError}
                            InputProps={{
                                endAdornment: isConsulting ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Autocomplete
                            freeSolo
                            options={fornecedores.map((option) => option.nome_razao_social)}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option} <span style={{ color: 'gray', fontSize: '0.8em', marginLeft: '8px' }}>(Cadastrado)</span>
                                </li>
                            )}
                            value={newNome}
                            onInputChange={(event, newInputValue) => {
                                setNewNome(newInputValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Nome/Razão Social"
                                    fullWidth
                                    required
                                />
                            )}
                        />
                        <TextField
                            label="Telefone"
                            fullWidth
                            value={newTelefone}
                            onChange={(e) => setNewTelefone(e.target.value)}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                        <TextField
                            label="Endereço"
                            fullWidth
                            value={newEndereco}
                            onChange={(e) => setNewEndereco(e.target.value)}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Tipo de Fornecimento</InputLabel>
                            <Select
                                value={newTipo}
                                label="Tipo de Fornecimento"
                                onChange={(e) => setNewTipo(e.target.value as 'MATERIAL' | 'SERVICO' | 'AMBOS')}
                            >
                                <MenuItem value="SERVICO">Serviço</MenuItem>
                                <MenuItem value="MATERIAL">Material</MenuItem>
                                <MenuItem value="AMBOS">Ambos</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddModalOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveNewFornecedor} variant="contained" disabled={!!cpfCnpjError || isConsulting}>Adicionar</Button>
                </DialogActions>
            </Dialog>

            <FornecedorEditModal
                fornecedor={fornecedorToEdit}
                fornecedoresList={fornecedores}
                open={isEditModalOpen}
                onClose={() => setEditModalOpen(false)}
                onFornecedorUpdated={() => {
                    showSnackbar('Fornecedor atualizado com sucesso!', 'success');
                    fetchFornecedores();
                }}
            />

            {/* Lancamentos do Fornecedor Modal */}
            <Dialog open={isLancamentosModalOpen} onClose={() => setIsLancamentosModalOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>Lançamentos: {selectedFornecedorLancamentos?.nome_razao_social}</DialogTitle>
                <DialogContent dividers>
                    {selectedFornecedorLancamentos && (
                        <LancamentosTable 
                            filters={{ fornecedorId: selectedFornecedorLancamentos.id }} 
                            onEdit={() => {}} // Can be implemented if needed to edit lancamento directly from here
                            onDelete={() => {}} // Same
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsLancamentosModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={6000}
                onClose={() => setIsSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setIsSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </Container>
    );
};

export default FornecedoresPage;